import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge, PageHeader } from "@/components/common";
import { ShieldAlert, QrCode, Search, X, User, Pencil, Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const POIN_DEFAULT = { ringan: 5, sedang: 15, berat: 30 };

export default function PelanggaranInput() {
  const qc = useQueryClient();
  const { data: santri = [] } = useQuery({ queryKey: ["santri"], queryFn: async () => (await api.get("/santri")).data });
  const { data: pelanggaran = [] } = useQuery({ queryKey: ["pelanggaran"], queryFn: async () => (await api.get("/pelanggaran")).data });

  const [form, setForm] = useState({ santri_id: "", jenis: "", kategori: "ringan", poin: 5, keterangan: "", penanganan: "" });
  const [tanggal, setTanggal] = useState(today());
  const [search, setSearch] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [editRec, setEditRec] = useState(null);

  const nameOf = (id) => santri.find((s) => s.id === id)?.nama || "-";
  const selected = santri.find((s) => s.id === form.santri_id) || null;

  const filtered = search.trim()
    ? santri.filter((s) => {
        const q = search.trim().toLowerCase();
        return (s.nama || "").toLowerCase().includes(q) || String(s.no_induk_pondok || "").toLowerCase().includes(q) || String(s.nidi || "").toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  const pick = (s) => { setForm((f) => ({ ...f, santri_id: s.id })); setSearch(""); };

  const resolveQr = (text) => {
    const t = String(text).trim().toUpperCase();
    if (!t) return { ok: false };
    const found = santri.find((s) => {
      const nidi = String(s.nidi || "").toUpperCase();
      const ni = String(s.no_induk_pondok || "").toUpperCase();
      const nm = String(s.nama || "").toUpperCase();
      return (nidi && (t === nidi || t.includes(nidi))) || (ni && (t === ni || t.includes(ni))) || (nm && (t === nm || t.includes(nm)));
    });
    if (found) {
      setForm((f) => ({ ...f, santri_id: found.id }));
      setScanOpen(false);
      toast.success(`Santri dipilih: ${found.nama}`);
      return { ok: true, nama: found.nama };
    }
    toast.error("Santri tidak ditemukan dari QR");
    return { ok: false, notfound: true };
  };

  const mut = useMutation({
    mutationFn: async () => api.post("/pelanggaran", { ...form, tanggal }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pelanggaran"] });
      qc.invalidateQueries({ queryKey: ["santri"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      toast.success("Pelanggaran tercatat");
      setForm({ santri_id: "", jenis: "", kategori: "ringan", poin: 5, keterangan: "", penanganan: "" });
      setTanggal(today());
      setSearch("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal menyimpan"),
  });

  const invalidatePel = () => {
    qc.invalidateQueries({ queryKey: ["pelanggaran"] });
    qc.invalidateQueries({ queryKey: ["santri"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["activities"] });
  };
  const editMut = useMutation({
    mutationFn: async () => api.put(`/pelanggaran/${editRec.id}`, editRec),
    onSuccess: () => { invalidatePel(); toast.success("Pelanggaran diperbarui"); setEditRec(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal memperbarui"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/pelanggaran/${id}`),
    onSuccess: () => { invalidatePel(); toast.success("Pelanggaran dihapus"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal menghapus"),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <div className="lg:col-span-2 space-y-5">
        <PageHeader icon={ShieldAlert} title="Input Poin Pelanggaran" subtitle="Catat pelanggaran & poin kedisiplinan santri." />
        <div className="glass rounded-2xl p-5 space-y-4">
          {/* Santri: cari nama / no induk + scan QR */}
          <div className="space-y-1.5">
            <Label className="text-xs">Santri</Label>
            {selected ? (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2" data-testid="pelanggaran-selected-santri">
                <User className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-emerald-950 dark:text-emerald-50 truncate text-sm">{selected.nama}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-300 truncate">{selected.no_induk_pondok} · {selected.departemen} · {selected.ribath}</p>
                </div>
                <button type="button" data-testid="pelanggaran-clear-santri" onClick={() => setForm((f) => ({ ...f, santri_id: "" }))} className="text-slate-400 hover:text-rose-600 p-1"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input data-testid="pelanggaran-search-input" placeholder="Cari nama / No. Induk / NIDI…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                  </div>
                  <Button type="button" variant="outline" data-testid="pelanggaran-scan-qr-button" onClick={() => setScanOpen(true)} className="gap-1.5 shrink-0 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-white/5">
                    <QrCode className="h-4 w-4" /> Scan QR
                  </Button>
                </div>
                {search.trim() && (
                  <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#202620] overflow-hidden max-h-64 overflow-y-auto" data-testid="pelanggaran-search-results">
                    {filtered.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">Tidak ada santri cocok.</p>}
                    {filtered.map((s) => (
                      <button type="button" key={s.id} data-testid={`pelanggaran-result-${s.no_induk_pondok}`} onClick={() => pick(s)}
                        className="w-full text-left px-3 py-2 hover:bg-emerald-50 dark:hover:bg-white/5 border-b last:border-0 border-slate-100 dark:border-white/10">
                        <p className="text-sm font-medium text-emerald-950 dark:text-emerald-50 truncate">{s.nama}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-300 truncate">{s.no_induk_pondok} · {s.departemen} · {s.ribath}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tanggal otomatis (mengikuti kalender), bisa diubah */}
          <div className="space-y-1.5">
            <Label className="text-xs">Tanggal</Label>
            <Input type="date" data-testid="pelanggaran-tanggal-input" value={tanggal} max={today()} onChange={(e) => setTanggal(e.target.value || today())} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Jenis Pelanggaran</Label>
            <Input data-testid="pelanggaran-jenis-input" placeholder="mis. Terlambat Jamaah" value={form.jenis} onChange={(e) => setForm({ ...form, jenis: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Kategori</Label>
              <Select value={form.kategori} onValueChange={(v) => setForm({ ...form, kategori: v, poin: POIN_DEFAULT[v] })}>
                <SelectTrigger data-testid="pelanggaran-kategori-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ringan">Ringan</SelectItem><SelectItem value="sedang">Sedang</SelectItem><SelectItem value="berat">Berat</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Poin</Label>
              <Input type="number" value={form.poin} onChange={(e) => setForm({ ...form, poin: +e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Keterangan</Label><Textarea value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Penanganan</Label><Input placeholder="mis. Teguran lisan" value={form.penanganan} onChange={(e) => setForm({ ...form, penanganan: e.target.value })} /></div>
          <Button data-testid="admin-submit-pelanggaran-button" onClick={() => mut.mutate()} disabled={mut.isPending || !form.santri_id || !form.jenis}
            className="w-full h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800">Catat Pelanggaran</Button>
        </div>
      </div>

      <div className="lg:col-span-3 space-y-3">
        <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">Riwayat Pelanggaran Terbaru</h4>
        {pelanggaran.length === 0 && <div className="glass rounded-2xl p-8 text-center text-slate-400">Belum ada pelanggaran.</div>}
        {pelanggaran.map((p) => (
          <div key={p.id} className="glass rounded-2xl p-4 flex items-start gap-3" data-testid={`pelanggaran-item-${p.id}`}>
            <div className="h-10 w-10 rounded-xl bg-rose-100 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0"><ShieldAlert className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-emerald-950 dark:text-emerald-50">{nameOf(p.santri_id)}</p>
                <StatusBadge status={p.kategori} />
                <span className="text-xs font-bold text-rose-600">-{p.poin} poin</span>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">{p.jenis}{p.keterangan ? ` — ${p.keterangan}` : ""}</p>
              <p className="text-xs text-slate-400 mt-1">{p.tanggal}{p.penanganan ? ` · ${p.penanganan}` : ""}</p>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`pelanggaran-edit-${p.id}`} onClick={() => setEditRec({ ...p })}><Pencil className="h-4 w-4 text-emerald-700 dark:text-emerald-300" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`pelanggaran-delete-${p.id}`} onClick={() => { if (window.confirm(`Hapus pelanggaran "${p.jenis}" milik ${nameOf(p.santri_id)}? Poin santri akan dikembalikan.`)) delMut.mutate(p.id); }}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editRec} onOpenChange={(o) => !o && setEditRec(null)}>
        <DialogContent data-testid="pelanggaran-edit-dialog">
          <DialogHeader><DialogTitle>Edit Pelanggaran</DialogTitle><DialogDescription>{editRec ? nameOf(editRec.santri_id) : ""}</DialogDescription></DialogHeader>
          {editRec && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Tanggal</Label><Input type="date" max={today()} value={editRec.tanggal} onChange={(e) => setEditRec({ ...editRec, tanggal: e.target.value || today() })} data-testid="pelanggaran-edit-tanggal" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Jenis Pelanggaran</Label><Input value={editRec.jenis} onChange={(e) => setEditRec({ ...editRec, jenis: e.target.value })} data-testid="pelanggaran-edit-jenis" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Kategori</Label>
                  <Select value={editRec.kategori} onValueChange={(v) => setEditRec({ ...editRec, kategori: v, poin: POIN_DEFAULT[v] ?? editRec.poin })}>
                    <SelectTrigger data-testid="pelanggaran-edit-kategori"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="ringan">Ringan</SelectItem><SelectItem value="sedang">Sedang</SelectItem><SelectItem value="berat">Berat</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Poin</Label><Input type="number" value={editRec.poin} onChange={(e) => setEditRec({ ...editRec, poin: +e.target.value })} data-testid="pelanggaran-edit-poin" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Keterangan</Label><Input value={editRec.keterangan || ""} onChange={(e) => setEditRec({ ...editRec, keterangan: e.target.value })} data-testid="pelanggaran-edit-keterangan" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditRec(null)}>Batal</Button><Button data-testid="pelanggaran-edit-save" onClick={() => editMut.mutate()} disabled={editMut.isPending || !editRec?.jenis} className="bg-emerald-700 hover:bg-emerald-800">Simpan Perubahan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <QRScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onScan={resolveQr} />
    </div>
  );
}

function QRScanDialog({ open, onClose, onScan }) {
  const [err, setErr] = useState("");
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const lastScan = useRef({ t: "", at: 0 });

  useEffect(() => {
    if (!open) return;
    setErr("");
    let scanner;
    let cancelled = false;
    const cfg = { fps: 10, qrbox: (vw, vh) => { const m = Math.floor(Math.min(vw, vh) * 0.7); return { width: m, height: m }; }, aspectRatio: 1.0 };
    const onDecode = (text) => {
      const t = String(text).trim();
      const now = Date.now();
      if (t === lastScan.current.t && now - lastScan.current.at < 2500) return;
      lastScan.current = { t, at: now };
      onScanRef.current && onScanRef.current(text);
    };
    import("html5-qrcode").then(async ({ Html5Qrcode }) => {
      if (cancelled) return;
      scanner = new Html5Qrcode("qr-reader-pelanggaran");
      const startWith = async (camera) => scanner.start(camera, cfg, onDecode, () => {});
      try {
        const cams = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (cams && cams.length) {
          const back = cams.find((c) => /back|rear|environment/i.test(c.label));
          await startWith({ deviceId: { exact: (back || cams[cams.length - 1]).id } });
        } else {
          await startWith({ facingMode: "environment" });
        }
      } catch (e1) {
        try { await startWith({ facingMode: "user" }); }
        catch (e2) {
          const msg = (e2 && e2.name === "NotAllowedError") || /permission|denied/i.test(String(e2))
            ? "Akses kamera ditolak. Izinkan kamera untuk situs ini di pengaturan browser, lalu coba lagi."
            : "Tidak dapat mengakses kamera. Pastikan tidak ada aplikasi lain yang memakainya.";
          setErr(msg); toast.error(msg);
        }
      }
    }).catch(() => { setErr("Gagal memuat pemindai."); });
    return () => {
      cancelled = true;
      if (scanner) {
        try {
          if (scanner.isScanning) scanner.stop().then(() => scanner.clear()).catch(() => {});
          else scanner.clear();
        } catch (e) { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pelanggaran-qr-scan-dialog">
        <DialogHeader>
          <DialogTitle>Scan QR Santri</DialogTitle>
          <DialogDescription>Arahkan kamera ke QR Code santri untuk memilih otomatis.</DialogDescription>
        </DialogHeader>
        <div id="qr-reader-pelanggaran" style={{ width: "100%", borderRadius: 12, overflow: "hidden" }} />
        {err && <p className="text-xs text-rose-600 text-center" data-testid="pelanggaran-qr-scan-error">{err}</p>}
        <p className="text-xs text-slate-500 dark:text-slate-300 text-center">Arahkan kamera ke QR Code santri (berisi NIDI / No. Induk / Nama). Santri yang cocok akan otomatis terpilih.</p>
      </DialogContent>
    </Dialog>
  );
}
